import { apiFetch } from './client';

// =============================================================================
// Polymorphic Commenting System — API adapter (DEV-720 / DEV-722)
// Mirrors CommentsController. Comment bodies are sanitized on RENDER
// (sanitizeRichText), never here.
// =============================================================================

export type CommentContextMode = 'project' | 'social';

export interface CommentReactionGroup {
  reactionType: string;
  count: number;
  mineReacted: boolean;
}

export interface CommentDto {
  id: string;
  anchorType: string;
  anchorId: string;
  parentCommentId: string | null;
  depth: number;
  authorGuid: string;
  authorName: string | null;
  authorAvatarUrl: string | null;
  body: string;
  bodyFormat: 'plain' | 'richtext';
  isDeleted: boolean;
  deletedByKind: 'author' | 'moderator' | null;
  edited: boolean;
  canEdit: boolean;
  createdAt: string;
  editedAt: string | null;
  reactions: CommentReactionGroup[];
}

export interface ReactionVocabItem {
  reactionType: string;
  label: string;
  sortOrder: number;
}

export interface CommentThread {
  anchorType: string;
  anchorId: string;
  contextMode: CommentContextMode;
  reactionVocab: ReactionVocabItem[];
  /** Post-level reaction tallies (the heart + count on the post itself). */
  postReactions: CommentReactionGroup[];
  comments: CommentDto[];
}

export interface AnchorUnread {
  anchorType: string;
  anchorId: string;
  unreadCount: number;
  totalCount: number;
  postReactions: CommentReactionGroup[];
  reactionVocab: ReactionVocabItem[];
}

export function fetchThread(anchorType: string, anchorId: string): Promise<CommentThread> {
  return apiFetch<CommentThread>(`/api/comments/${anchorType}/${anchorId}`);
}

export function createComment(
  anchorType: string,
  anchorId: string,
  body: string,
  parentCommentId?: string | null,
): Promise<CommentDto> {
  return apiFetch<CommentDto>(`/api/comments/${anchorType}/${anchorId}`, {
    method: 'POST',
    body: JSON.stringify({ body, parentCommentId: parentCommentId ?? null }),
  });
}

export function editComment(commentId: string, body: string): Promise<CommentDto> {
  return apiFetch<CommentDto>(`/api/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

export function deleteComment(commentId: string): Promise<void> {
  return apiFetch<void>(`/api/comments/${commentId}`, { method: 'DELETE' });
}

export function addReaction(commentId: string, reactionType: string): Promise<void> {
  return apiFetch<void>(`/api/comments/${commentId}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ reactionType }),
  });
}

export function removeReaction(commentId: string, reactionType: string): Promise<void> {
  return apiFetch<void>(
    `/api/comments/${commentId}/reactions?reactionType=${encodeURIComponent(reactionType)}`,
    { method: 'DELETE' },
  );
}

// Post-level (anchor) reactions — react to the post itself, not a comment.
export function reactToPost(anchorType: string, anchorId: string, reactionType: string): Promise<void> {
  return apiFetch<void>(`/api/comments/${anchorType}/${anchorId}/reactions`, {
    method: 'POST',
    body: JSON.stringify({ reactionType }),
  });
}

export function unreactFromPost(anchorType: string, anchorId: string, reactionType: string): Promise<void> {
  return apiFetch<void>(
    `/api/comments/${anchorType}/${anchorId}/reactions?reactionType=${encodeURIComponent(reactionType)}`,
    { method: 'DELETE' },
  );
}

export function markThreadRead(anchorType: string, anchorId: string): Promise<void> {
  return apiFetch<void>(`/api/comments/${anchorType}/${anchorId}/mark-read`, { method: 'POST' });
}

export function fetchUnreadCount(anchorType: string, anchorId: string): Promise<AnchorUnread> {
  return apiFetch<AnchorUnread>(`/api/comments/${anchorType}/${anchorId}/unread-count`);
}

export type CommentFlagType = 'inappropriate' | 'spam' | 'off_topic' | 'other';

export function reportComment(
  commentId: string,
  flagType: CommentFlagType,
  reason?: string | null,
): Promise<void> {
  return apiFetch<void>(`/api/comments/${commentId}/flags`, {
    method: 'POST',
    body: JSON.stringify({ flagType, reason: reason ?? null }),
  });
}

// ── Moderation (system admin) ────────────────────────────────────────────────

export interface CommentFlag {
  id: string;
  commentId: string;
  reporterGuid: string;
  flagType: string;
  reason: string | null;
  status: string;
  createdAt: string;
  anchorType: string;
  anchorId: string;
  commentBody: string;
  commentAuthorGuid: string;
  commentDeleted: boolean;
}

export function fetchModerationFlags(
  status: 'open' | 'reviewed' | 'dismissed' = 'open',
): Promise<CommentFlag[]> {
  return apiFetch<CommentFlag[]>(`/api/comments/moderation/flags?status=${status}`);
}

export function resolveFlag(flagId: string, status: 'reviewed' | 'dismissed'): Promise<void> {
  return apiFetch<void>(`/api/comments/flags/${flagId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}
